import { useEffect, useRef, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Clock, X } from 'lucide-react';

// Popup calendar + 12-hour time picker. Works in a naive wall-clock string
// (YYYY-MM-DDTHH:MM) and labels the value as ET. Callers convert to/from UTC at
// their form boundary (see lib/eastern-time.js).
export default function DateTimePicker({ value, onChange, dropUp: forceDropUp }) {
  const containerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(!!forceDropUp);

  // Decide which way the ~360px popup opens. Callers can force upward (e.g. when
  // the field sits at the bottom of a modal, so the calendar stays inside the
  // card); otherwise auto-flip up only when there's no room below in the viewport.
  const toggleOpen = () => {
    setOpen((o) => {
      const next = !o;
      if (next && containerRef.current) {
        if (forceDropUp != null) {
          setDropUp(forceDropUp);
        } else {
          const rect = containerRef.current.getBoundingClientRect();
          setDropUp(window.innerHeight - rect.bottom < 380);
        }
      }
      return next;
    });
  };

  const parsed = value ? new Date(value) : null;
  const now = new Date();
  const [viewYear, setViewYear] = useState(parsed?.getFullYear() ?? now.getFullYear());
  const [viewMonth, setViewMonth] = useState(parsed?.getMonth() ?? now.getMonth());

  useEffect(() => {
    if (value) {
      const d = new Date(value);
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
    }
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toIso = (d) => {
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${mo}-${da}T${hh}:${mi}`;
  };

  const h24 = parsed ? parsed.getHours() : 9;
  const min = parsed ? parsed.getMinutes() : 0;
  const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
  const ampm = h24 >= 12 ? 'PM' : 'AM';
  const to24 = (h, ap) => ap === 'AM' ? (h === 12 ? 0 : h) : (h === 12 ? 12 : h + 12);

  const emit = (date, hours, minutes) => {
    const d = new Date(date);
    d.setHours(hours, minutes, 0, 0);
    onChange(toIso(d));
  };

  const baseDate = () => parsed || new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const selectDay = (day) => emit(new Date(viewYear, viewMonth, day), h24, min);
  const onHour = (newH12) => emit(baseDate(), to24(newH12, ampm), min);
  const onMinute = (m) => emit(baseDate(), h24, m);
  const onAmPm = (ap) => { if (ap !== ampm) emit(baseDate(), to24(h12, ap), min); };

  // Calendar grid
  const firstDow = new Date(viewYear, viewMonth, 1).getDay();
  const dim = new Date(viewYear, viewMonth + 1, 0).getDate();
  const pdim = new Date(viewYear, viewMonth, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push({ day: pdim - firstDow + 1 + i, cur: false });
  for (let i = 1; i <= dim; i++) cells.push({ day: i, cur: true });
  while (cells.length < 42) cells.push({ day: cells.length - firstDow - dim + 1, cur: false });

  const isSel = (d) => parsed && parsed.getFullYear() === viewYear && parsed.getMonth() === viewMonth && parsed.getDate() === d;
  const isTod = (d) => now.getFullYear() === viewYear && now.getMonth() === viewMonth && now.getDate() === d;
  const prevMo = () => { if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); } else setViewMonth(m => m - 1); };
  const nextMo = () => { if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); } else setViewMonth(m => m + 1); };

  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  // Include current minute in options even if not a multiple of 5
  const minuteOpts = Array.from({ length: 12 }, (_, i) => i * 5);
  if (parsed && !minuteOpts.includes(min)) { minuteOpts.push(min); minuteOpts.sort((a, b) => a - b); }

  const display = parsed
    ? parsed.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
      + '  ·  '
      + parsed.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
      + ' ET'
    : '';

  return (
    <div ref={containerRef} className="relative">
      <div
        className="input w-full flex items-center gap-2 cursor-pointer"
        onClick={toggleOpen}
      >
        <Calendar size={16} className="text-slate-400 shrink-0" />
        {display
          ? <span className="text-slate-900 truncate flex-1">{display}</span>
          : <span className="text-slate-400 flex-1">Select date & time…</span>
        }
        {parsed && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange(''); }}
            className="text-slate-300 hover:text-slate-500 shrink-0"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && (
        <div className={`absolute z-50 ${dropUp ? 'bottom-full mb-1' : 'top-full mt-1'} bg-white rounded-xl shadow-xl border border-slate-200 p-4 w-[320px]`}>
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-3">
            <button type="button" onClick={prevMo} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
              <ChevronLeft size={16} className="text-slate-600" />
            </button>
            <span className="font-semibold text-sm text-slate-800">{MONTHS[viewMonth]} {viewYear}</span>
            <button type="button" onClick={nextMo} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
              <ChevronRight size={16} className="text-slate-600" />
            </button>
          </div>

          {/* Day-of-week headers */}
          <div className="grid grid-cols-7 mb-1">
            {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
              <div key={d} className="text-center text-[11px] font-semibold text-slate-400 py-1">{d}</div>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7">
            {cells.map((c, i) => (
              <button
                key={i}
                type="button"
                disabled={!c.cur}
                onClick={() => c.cur && selectDay(c.day)}
                className={`
                  h-9 rounded-full text-sm flex items-center justify-center transition-all
                  ${!c.cur ? 'text-slate-200' : ''}
                  ${c.cur && isSel(c.day) ? 'bg-brand text-white font-bold' : ''}
                  ${c.cur && !isSel(c.day) && isTod(c.day) ? 'ring-2 ring-brand/30 text-brand font-semibold' : ''}
                  ${c.cur && !isSel(c.day) && !isTod(c.day) ? 'text-slate-700 hover:bg-slate-100' : ''}
                `}
              >
                {c.day}
              </button>
            ))}
          </div>

          {/* Time picker */}
          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2">
            <Clock size={14} className="text-slate-400 shrink-0" />
            <select
              value={h12}
              onChange={(e) => onHour(parseInt(e.target.value))}
              className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
            >
              {[12,1,2,3,4,5,6,7,8,9,10,11].map(h => (
                <option key={h} value={h}>{h}</option>
              ))}
            </select>
            <span className="text-slate-400 font-bold">:</span>
            <select
              value={min}
              onChange={(e) => onMinute(parseInt(e.target.value))}
              className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
            >
              {minuteOpts.map(m => (
                <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
              ))}
            </select>
            <div className="flex rounded-lg border border-slate-200 overflow-hidden ml-1">
              <button
                type="button"
                onClick={() => onAmPm('AM')}
                className={`px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                  ampm === 'AM' ? 'bg-brand text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                }`}
              >AM</button>
              <button
                type="button"
                onClick={() => onAmPm('PM')}
                className={`px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                  ampm === 'PM' ? 'bg-brand text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                }`}
              >PM</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
